<template>
  <div class="calendar-wrapper">
    <div class="custom-header">
      <div class="left">
        <b-button variant="outline-primary" @click="goToday">วันนี้</b-button>
        <div class="go-to-date-input">
          <datetime
            format="dd/MM/yyyy"
            v-model="selectedDate"
            ref="startDate"
            placeholder="กรุณาเลือกวันที่"
            @close="goTodate"
          >
          </datetime>
        </div>

        <div class="navigation">
          <b-button @click="goPrev" variant="text-primary">
            <font-awesome-icon :icon="['fas', 'chevron-left']" />
          </b-button>
          <b-button @click="goNext" variant="text-primary">
            <font-awesome-icon :icon="['fas', 'chevron-right']" />
          </b-button>
        </div>
        <div class="title">{{ currentTitle }}</div>
      </div>
      <div>
        <b-button-group>
          <b-button
            variant="outline-primary"
            :class="{ active: currentView === 'timeGridDay' }"
            @click="switchToDay"
            >D</b-button
          >
          <b-button
            variant="outline-primary"
            :class="{ active: currentView === 'timeGridWeek' }"
            @click="switchToWeek"
            >W</b-button
          >
        </b-button-group>
      </div>
    </div>
    <div ref="calendarWrapper" id="calendar-wrapper">
      <FullCalendar
        ref="calendar"
        class="app-calendar"
        :options="calendarOptions"
        :dayHeaderContent="customDayHeader"
        :header="false"
      >
        <template v-slot:eventContent="arg">
          <div
            v-if="arg.event.id == 'unavialable'"
            class="calendar-event unavialable"
          ></div>
          <div
            v-else
            :id="`popover-detail-${arg.event.id}`"
            @click="handleEventClick(arg.event.id)"
          >
            <div
              class="calendar-event"
              :style="{
                color: arg.event.extendedProps.calendarPrimaryColor,
                backgroundColor: arg.event.extendedProps.calendarSecondaryColor,
                borderColor: arg.event.extendedProps.calendarPrimaryColor,
              }"
            >
              <template v-if="currentView !== 'dayGridMonth'">
                <div>
                  <div>{{ arg.timeText }}</div>
                  <div>{{ arg.event.title }}</div>
                  <div>
                    ({{ arg.event.extendedProps.calendarProductDisplay }})
                  </div>
                </div>
                <div class="font-weight-bold name">
                  {{ arg.event.extendedProps.fullName }}
                </div>
              </template>
              <div v-else>
                <div>{{ `${arg.timeText} ${arg.event.title}` }}</div>
              </div>
            </div>
          </div>
        </template>
      </FullCalendar>
    </div>
    <TransactionDetailPopup
      ref="transactionDetailPopup"
      :placement="currentView === 'timeGridDay' ? 'auto' : 'right'"
      @confirmSuccess="$emit('getTransaction')"
    />
  </div>
</template>

<script>
import FullCalendar from "@fullcalendar/vue";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import TransactionDetailPopup from "./TransactionDetailPopup.vue";
import thLocale from "@fullcalendar/core/locales/th";

export default {
  components: {
    FullCalendar,
    TransactionDetailPopup,
  },
  props: {
    items: {
      required: true,
      type: Array,
    },
  },
  data() {
    return {
      selectedDate: this.$moment().format(),
      selectedEvent: {},
      show: false,
      calendarOptions: {
        locale: "th",
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
        headerToolbar: false,
        initialView: "timeGridWeek",
        initialEvents: [],
        dayMaxEventRows: true,
        dayHeaderContent: this.customDayHeader,
        datesSet: this.updateTitle,
        titleFormat: { year: "numeric", month: "long" },
        allDaySlot: false,
        events: this.items,
        datesSet: this.datesSet,
        views: {
          timeGridWeek: {
            dayMaxEventRows: 3,
          },
          dayGridMonth: {
            dayMaxEventRows: 3,
          },
        },
        slotLabelFormat: {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        },
        eventTimeFormat: {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        },
        slotMinTime: "08:00:00",
        slotMaxTime: "22:00:00",
        height: "auto",
      },
      currentEvents: [],
      currentTitle: "",
      currentView: "timeGridWeek",
      bookingDetail: {},
      isLoading: false,
      dataPayment: null,
    };
  },
  watch: {
    items(val) {
      this.calendarOptions.events = val;
    },
  },
  methods: {
    formatNumber(val) {
      return this.$services.utils.formatNumber(val);
    },

    datesSet(info) {
      this.$emit("changeViewRange", this.currentView, {
        startTime: this.$moment(info.start).format("YYYY-MM-DD"),
        endTime: this.$moment(info.end).format("YYYY-MM-DD"),
      });
    },

    convertTime(time) {
      let getTime = `${this.$moment().format("YYYY-MM-DDT")}${time}`;

      return getTime;
    },

    switchToMonth() {
      this.$refs.calendar.getApi().changeView("dayGridMonth");
      this.currentView = "dayGridMonth";
    },
    switchToWeek() {
      this.$refs.calendar.getApi().changeView("timeGridWeek");
      this.currentView = "timeGridWeek";
    },
    switchToDay() {
      this.$refs.calendar.getApi().changeView("timeGridDay");
      this.currentView = "timeGridDay";
    },

    goPrev() {
      this.$refs.calendar.getApi().prev();
    },
    goNext() {
      this.$refs.calendar.getApi().next();
    },
    goTodate() {
      if (this.selectedDate) {
        this.switchToDay();
        this.$refs.calendar.getApi().gotoDate(this.selectedDate);
      }
    },
    goToday() {
      this.$refs.calendar.getApi().today();
    },
    updateTitle(arg) {
      this.currentTitle = arg.view.title;
    },
    customSlotLabel(arg) {
      const hour = arg.date.getHours();
      return {
        html: arg.date.toLocaleTimeString("th-TH", {
          hour: "numeric",
          hour12: true,
        }),
      };
    },
    customDayHeader(arg) {
      const weekday = arg.date.toLocaleDateString("th", { weekday: "short" });
      const day = arg.date.getDate();
      return {
        html: `<div class="day-name">${weekday}</div><div class="day-number">${day}</div>`,
      };
    },

    confirmSuccess() {
      this.$emit("getTransaction");
      this.closePopover();
    },

    handleEventClick(id) {
      this.$refs.transactionDetailPopup.show(id);
    },
    closePopover() {
      this.$refs.transactionDetailPopup.hide();
    },
    clearFilter() {
      this.selectedDate = this.$moment().format();
      this.switchToWeek();
    },
  },
};
</script>

<style lang="scss">
.calendar-wrapper {
  border: 1px solid var(--fc-border-color);
  border-radius: 10px;
  background-color: white;

  .custom-header {
    display: flex;
    justify-content: space-between;
    padding: 16px 16px 0 16px;
    align-items: center;
    background-color: #fff;

    .title {
      font-size: 1.25rem;
    }

    .left {
      display: flex;
      gap: 16px;
      align-items: center;
    }

    .navigation {
      display: flex;
      gap: 4px;

      button {
        border-radius: 50%;
      }
    }
  }
}
.app-calendar {
  height: calc(100vh - 250px);
  overflow-x: auto;
  position: relative;
  min-height: 0 !important;

  thead {
    background-color: white;
  }

  table {
    border-color: transparent !important;
    border-spacing: 0;
  }

  table:not([cellpadding]) td {
    padding-left: 0;
    padding-right: 0;
  }

  th {
    padding-bottom: 0 !important;
    border: none;
    border-bottom: 1px solid var(--fc-border-color);
  }

  .fc-col-header-cell-cushion {
    flex: 1 1;
    display: flex;
    flex-flow: column;
    align-items: center;
    padding: 12px 0;
    gap: 4px;

    .day-name {
      text-transform: uppercase;
      font-size: 0.75rem;
      color: #79747e;
      font-weight: 500;
    }

    .day-number {
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.25rem;
      font-weight: 500;
      color: #79747e;
      height: 2em;
      width: 2em;
    }
  }
}

.fc .fc-timegrid-slot[data-time="00:00:00"] {
  .fc-timegrid-slot-label-cushion {
    display: none;
  }
}
.fc .fc-scrollgrid-section {
  height: 0;
}
.fc .fc-timegrid-divider {
  padding: 0;
  border: none;
}

.fc .fc-timegrid-axis-frame {
  display: none;
}

.fc .fc-timegrid-slot-minor {
  border: none;
}

.fc .fc-timegrid-slot[data-time="*"] {
  border: none;
}

.fc-scrollgrid-section > td {
  padding-top: 0 !important;
}

.fc .fc-timegrid-slot-label-cushion {
  color: #79747e;
}

.fc .fc-toolbar.fc-header-toolbar {
  margin: 0;
  padding: 16px;
}

.fc .fc-scrollgrid-section > * {
  border-right-width: 0px;
}

.fc-event {
  background-color: transparent;
  border: none;
  border-radius: 0;
  margin-left: -3px;
  margin-right: -6px;
  margin-top: -6px;
  margin-bottom: 6px;
  box-shadow: none !important;
}

.fc-v-event .fc-event-main {
  padding: 0;
  background-color: white;
}

.fc .fc-timegrid-slot {
  height: 3.5em;
}

.fc-timegrid-event .fc-event-main {
  padding: 0;
}

.calendar-event {
  height: 100%;
  border-left: 8px solid;
  padding: 8px 4px;
  cursor: pointer;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .name {
    font-weight: bold;
    margin-top: 8px;
    font-size: 14px;
    text-transform: uppercase;
  }

  &.unavialable {
    background-color: rgba(0, 0, 0, 0.04);
    background-image: repeating-linear-gradient(
      135deg,
      rgba(0, 0, 0, 0.08),
      rgba(0, 0, 0, 0.08) 8px,
      transparent 8px,
      transparent 16px
    );
    border-left-color: transparent;
    opacity: 0.9;
    cursor: not-allowed;
    pointer-events: none;
    border: none;
  }
}

.fc-direction-ltr .fc-daygrid-event.fc-event-end,
.fc-direction-rtl .fc-daygrid-event.fc-event-start {
  margin: 0;
}

.event-popover {
  position: absolute;
  background-color: white;
  padding: 0.75rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  width: 350px;
}
.fc .fc-daygrid-body table tbody tr {
  height: 130px; /* set fixed height, adjust as needed */
}

.fc .fc-daygrid-day-top {
  justify-content: center;
}

:root {
  --fc-border-color: #c4c7c5;
  --fc-today-bg-color: white;
}
.event-content {
  display: flex;
  flex-direction: column;
  gap: 12px;

  .info-wrapper {
    display: flex;
    flex-direction: column;
    gap: 4px;

    .info-title {
      font-weight: bold;
      color: black;
    }
  }

  .info-item {
    display: flex;
    justify-content: space-between;
    align-items: start;

    .value {
      font-weight: bold;
    }
  }
}

.go-to-date-input {
  display: flex;
  align-items: center;

  border: 1px solid var(--primary-color);
  border-radius: 0.25rem;
  padding: 0.375rem 0.75rem;
  height: 35px;
  width: 110px;
  cursor: pointer;
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out,
    border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  .vdatetime > input {
    height: auto;
    padding: 0;
    text-align: center;
    cursor: pointer;
  }

  &:hover {
    background-color: var(--primary-color);
    color: #fff;
    .vdatetime > input {
      color: #fff;
    }
  }
}
</style>
